import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateProductInput,
  createProductInputSchema,
  MIN_PRODUCT_SIZE_MM,
  PRODUCT_MEASUREMENT_KIND_LABELS,
  PRODUCT_MEASUREMENT_KINDS,
  type ProductMeasurementKind,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { type DefaultValues, useForm } from 'react-hook-form';
import { Button, Input, Label } from '@/shared/ui';

interface BasicInfoFormProps {
  readonly defaultValues?: DefaultValues<CreateProductInput>;
  readonly submitLabel: string;
  readonly isSubmitting: boolean;
  readonly onSubmit: (values: CreateProductInput) => void;
}

/** 입력칸 공통 — 높이 40 알약. */
const INPUT_CLASS = 'h-10';

/** 폼 안 묶음 제목(상품 정보·실측·제작 조건) — 작은 굵은 글자와 아래 얇은 선. */
const GROUP_TITLE_CLASS =
  'mb-4 w-full border-b border-foreground/[0.08] pb-2 text-sm font-semibold text-foreground';

/** 2열 그리드에 배치할 한 줄 입력 필드. (와이어프레임의 기본 정보 배열) */
const GRID_FIELDS = [
  ['leadTimeNote', '납기 안내', '발주 확정 후 영업일 기준 10~14일'],
  ['materialNote', '소재 정보', 'STS304 스테인리스 · PP 뚜껑'],
] as const;

/** 실측 규격 — 편집영역 표면 실측의 원천이라 숫자로 받는다(정책 §2-1). 원통이면 넓이 칸이 지름이다. */
const SIZE_FIELDS = [
  ['widthMm', { FLAT: '넓이 (mm)', CYLINDER: '지름 (mm)' }, '73'],
  ['heightMm', { FLAT: '높이 (mm)', CYLINDER: '높이 (mm)' }, '195'],
] as const satisfies readonly (readonly [
  'widthMm' | 'heightMm',
  Readonly<Record<ProductMeasurementKind, string>>,
  string,
])[];

/** 기본 정보 8필드(실측 종류·실측 2칸 포함) — 상품 생성의 최소 단위(계약과 동일한 zod 검증). */
export const BasicInfoForm = ({
  defaultValues,
  submitLabel,
  isSubmitting,
  onSubmit,
}: BasicInfoFormProps): ReactElement => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateProductInput>({
    resolver: zodResolver(createProductInputSchema),
    defaultValues: { ...defaultValues, measurementKind: defaultValues?.measurementKind ?? 'FLAT' },
  });
  const measurementKind = watch('measurementKind');

  return (
    // 다른 관리자 폼과 같이 브라우저 검증을 끄고 계약 zod 문구만 보인다 — 켜 두면 min 밖 값(0 등)이 제출 전에 막혀 문구가 안 나온다.
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-8">
      <fieldset className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
        <legend className={GROUP_TITLE_CLASS}>상품 정보</legend>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="product-name">상품명 *</Label>
          <Input
            id="product-name"
            className={INPUT_CLASS}
            placeholder="스테인리스 보온 텀블러 470ml"
            {...register('name')}
          />
          {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="product-min-order-qty">최소 주문 수량 *</Label>
          <Input
            id="product-min-order-qty"
            className={INPUT_CLASS}
            type="number"
            min={1}
            placeholder="100"
            {...register('minOrderQty', { valueAsNumber: true })}
          />
          {errors.minOrderQty ? (
            <p className="text-sm text-destructive">{errors.minOrderQty.message}</p>
          ) : null}
        </div>

        {GRID_FIELDS.map(([field, label, placeholder]) => (
          <div key={field} className="flex flex-col gap-1.5">
            <Label htmlFor={`product-${field}`}>{label} *</Label>
            <Input
              id={`product-${field}`}
              className={INPUT_CLASS}
              placeholder={placeholder}
              {...register(field)}
            />
            {errors[field] ? (
              <p className="text-sm text-destructive">{errors[field]?.message}</p>
            ) : null}
          </div>
        ))}
      </fieldset>

      <div className="flex flex-col">
        <p className={GROUP_TITLE_CLASS}>실측</p>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium">실측 종류</legend>
          {/* 평면 | 원통 전환 — 실제 radio 를 감추고 라벨을 알약 버튼처럼 보인다(키보드·보조기기는 radio 그대로). */}
          <div className="inline-flex w-fit rounded-full bg-foreground/[0.05] p-1">
            {PRODUCT_MEASUREMENT_KINDS.map((kind) => (
              <label key={kind} className="cursor-pointer">
                <input
                  type="radio"
                  value={kind}
                  className="peer sr-only"
                  {...register('measurementKind', { deps: ['widthMm'] })}
                />
                <span className="block rounded-full px-5 py-1.5 text-sm text-foreground/60 transition-colors peer-checked:bg-primary peer-checked:font-semibold peer-checked:text-brand-lime peer-focus-visible:ring-2 peer-focus-visible:ring-ring">
                  {PRODUCT_MEASUREMENT_KIND_LABELS[kind]}
                </span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            원통이면 넓이 대신 지름을 넣습니다. 편집영역을 저장한 뒤에는 실측 종류를 바꿀 수
            없습니다.
          </p>
        </fieldset>
      </div>

      <fieldset className="-mt-3 grid gap-x-5 gap-y-4 sm:grid-cols-2">
        <legend className="sr-only">실측 규격</legend>
        {SIZE_FIELDS.map(([field, labels, placeholder]) => (
          <div key={field} className="flex flex-col gap-1.5">
            <Label htmlFor={`product-${field}`}>{labels[measurementKind ?? 'FLAT']} *</Label>
            <Input
              id={`product-${field}`}
              className={INPUT_CLASS}
              type="number"
              min={MIN_PRODUCT_SIZE_MM}
              step="0.01"
              placeholder={placeholder}
              {...register(field, { valueAsNumber: true })}
            />
            {errors[field] ? (
              <p role="alert" className="text-sm text-destructive">
                {errors[field]?.message}
              </p>
            ) : null}
          </div>
        ))}
      </fieldset>

      <div className="flex flex-col">
        <p className={GROUP_TITLE_CLASS}>제작 조건</p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="product-productionNote">제작 조건 안내 *</Label>
          <textarea
            id="product-productionNote"
            rows={4}
            placeholder="로고 1도 기준 · 2도 이상은 별도 협의 등 고객이 사전에 알아야 할 조건"
            className="rounded-2xl border border-input bg-background px-4 py-3 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            {...register('productionNote')}
          />
          {errors.productionNote ? (
            <p className="text-sm text-destructive">{errors.productionNote.message}</p>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end border-t border-foreground/[0.08] pt-6">
        <Button type="submit" isLoading={isSubmitting} className="h-10 px-6">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
